// EventSource subscription to GET /runs/:id/stream. Reconnects with the last
// seen row id on close/error while `active`; the server closes the stream
// itself once the run is terminal and drained.
import { useEffect, useRef, useState } from "react";
import { sphinxApi } from "@/lib/sphinx-api";
import {
  initialSphinxRunStreamState,
  parseSphinxEvent,
  reduceSphinxEvent,
  SPHINX_EVENT_TYPES,
  type SphinxRunStreamState,
} from "./sphinx-run-stream-reducer";

const RECONNECT_MS = 1500;

export function useSphinxRunStream(
  runId: string | null,
  active: boolean
): SphinxRunStreamState {
  const [state, setState] = useState<SphinxRunStreamState>(
    initialSphinxRunStreamState
  );
  const lastIdRef = useRef(0);
  const completeRef = useRef(false);

  // New run → fresh state.
  useEffect(() => {
    lastIdRef.current = 0;
    completeRef.current = false;
    setState(initialSphinxRunStreamState);
  }, [runId]);

  useEffect(() => {
    if (!runId || !active || typeof EventSource === "undefined") return;
    let es: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      es = new EventSource(sphinxApi.runs.streamUrl(runId, lastIdRef.current));
      const onEvent = (raw: Event) => {
        const msg = raw as MessageEvent<string>;
        const event = parseSphinxEvent(msg.data);
        if (!event) return;
        const id = Number(msg.lastEventId);
        if (Number.isFinite(id) && id > lastIdRef.current) lastIdRef.current = id;
        if (event.type === "complete") completeRef.current = true;
        setState((prev) => reduceSphinxEvent(prev, event, lastIdRef.current));
      };
      for (const type of SPHINX_EVENT_TYPES) es.addEventListener(type, onEvent);
      es.onerror = () => {
        es?.close();
        es = null;
        // The server ends the stream after `complete`; anything else is a
        // drop — resume from the last id.
        if (stopped || completeRef.current) return;
        timer = setTimeout(connect, RECONNECT_MS);
      };
    };
    connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      es?.close();
    };
  }, [runId, active]);

  return state;
}
